import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Chip,
  Autocomplete,
  Menu,
  ListItemIcon,
  IconButton,
  Tooltip,
  CircularProgress,
  Radio,
  Dialog,
  DialogContent,
} from '@mui/material';
import { 
  Search as SearchIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalance as AccountBalanceIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  FilterAlt,
  DateRange,
  Today,
  CalendarMonth,
  FileDownload,
} from '@mui/icons-material';
import { getCustomerTransactionLedger } from '../../services/reports';
import ExportButton from '../../components/ExportButton/ExportButton';
import RefreshButton from '../../components/RefreshButton/RefreshButton';
import FormatBadge from '../../components/FormatBadge/FormatBadge';
import { showSnackbar } from '../../components/Snackbar';
import { exportAsPDF, exportAsCSV, exportAsExcel } from '../../utils/importExport';
import AlertModal from '../../components/AlertModal';


const CustomerLedger = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    selectedCustomer: '',
    fromDate: '',
    toDate: '',
    searchTerm: '',
  });
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Export Dialog States
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportType, setExportType] = useState('');
  const [exportDateMode, setExportDateMode] = useState('current');
  const [exportFromDate, setExportFromDate] = useState('');
  const [exportToDate, setExportToDate] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const [alertModal, setAlertModal] = useState({ open: false, title: 'Alert', message: '' });
  const closeAlert = () => setAlertModal({ open: false, title: 'Alert', message: '' });


  // Customer dropdown states
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerSearchInput, setCustomerSearchInput] = useState('');
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const customerFetchingRef = useRef(false);
  const customerInitialLoadRef = useRef(false);
  const [customerPage, setCustomerPage] = useState(1);
  const [customerHasMore, setCustomerHasMore] = useState(true);
  const customerSearchTermRef = useRef('');

  // Ledger data states
  const [ledgerData, setLedgerData] = useState([]);
  const [loadingLedger, setLoadingLedger] = useState(false);
  const [ledgerError, setLedgerError] = useState('');

  // Fetch customers for autocomplete with pagination
  const fetchCustomers = useCallback(async (searchTerm = '', pageNum = 1, append = false) => {
    if (customerFetchingRef.current) return;
    if (!append && !customerHasMore && pageNum > 1) return;
    
    customerFetchingRef.current = true;
    setLoadingCustomers(true);
    try {
      const base = process.env.REACT_APP_API_BASE_URL;
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: String(pageNum),
        limit: '20',
      });
      
      if (searchTerm && searchTerm.trim().length > 0) {
        params.append('search', searchTerm.trim());
      }

      const response = await fetch(`${base}/api/customers?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      
      const result = await response.json();
      
      let customers = [];
      let total = 0;
      
      if (result.success && Array.isArray(result.data)) {
        customers = result.data;
        total = parseInt(result.extra?.total || customers.length, 10);
      }

      const formattedCustomers = customers.map((customer) => ({
        id: customer._id || customer.id,
        name: customer.name || '',
        number: String(customer.customerNo || ''),
        mobile: String(customer.phone || customer.whatsapp || ''),
        email: customer.email || '',
        customerId: customer._id || customer.id,
        _raw: customer, // Store raw data
      }));

      if (append) {
        setCustomerOptions(prev => {
          const newOptions = [...prev, ...formattedCustomers];
          const currentTotal = newOptions.length;
          const hasMore = currentTotal < total && formattedCustomers.length >= 20;
          setCustomerHasMore(hasMore);
          return newOptions;
        });
      } else {
        setCustomerOptions(formattedCustomers);
        const currentTotal = formattedCustomers.length;
        const hasMore = currentTotal < total && formattedCustomers.length >= 20;
        setCustomerHasMore(hasMore);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
      if (!append) {
        setCustomerOptions([]);
        setCustomerHasMore(false);
      }
    } finally {
      setLoadingCustomers(false);
      customerFetchingRef.current = false;
    }
  }, []);

  // Fetch customer transaction ledger
  const fetchCustomerLedger = useCallback(async () => {
    if (!selectedCustomer || !selectedCustomer.id) {
      setLedgerError('Please select a customer');
      return;
    }

    setLoadingLedger(true);
    setLedgerError('');
    try {
      const params = {
        customerId: selectedCustomer.id,
      };

      if (filters.fromDate) {
        params.fromDate = filters.fromDate;
      }
      if (filters.toDate) {
        params.toDate = filters.toDate;
      }

      const response = await getCustomerTransactionLedger(params);

      if (response.success && Array.isArray(response.data)) {
        // Map API response to ledger format
        const mappedData = response.data.map((item, index) => {
          return {
            sno: index + 1,
            date: item.date ? new Date(item.date).toISOString().split('T')[0] : '',
            particular: item.receivedType || 'Transaction',
            debit: item.transactionType === 'add' ? item.amount : 0,
            credit: item.transactionType !== 'add' ? item.amount : 0,
            customerId: item.customerId,
            _raw: item,
          };
        });

        // Sort by date ascending (oldest first) for correct balance calculation
        mappedData.sort((a, b) => new Date(a.date) - new Date(b.date));
        
        // Calculate running balance
        let runningBalance = 0;
        mappedData.forEach((item) => {
          runningBalance = runningBalance + item.debit - item.credit;
          item.balance = runningBalance;
        });

        // Sort by date descending (newest first) for display
        mappedData.sort((a, b) => new Date(b.date) - new Date(a.date));

        setLedgerData(mappedData);
        setPage(0); // Reset to first page
      } else {
        setLedgerData([]);
      }
    } catch (error) {
      console.error('Error fetching customer ledger:', error);
      setLedgerError(error.message || 'Failed to fetch customer ledger');
      setLedgerData([]);
    } finally {
      setLoadingLedger(false);
    }
  }, [selectedCustomer, filters.fromDate, filters.toDate]);

  // Auto-fetch ledger when selected customer changes
  useEffect(() => {
    if (selectedCustomer && selectedCustomer.id) {
      fetchCustomerLedger();
    }
  }, [selectedCustomer, fetchCustomerLedger]);

  // Pre-load customers on mount so dropdown is ready immediately
  useEffect(() => {
    fetchCustomers('', 1, false);
    customerInitialLoadRef.current = true;
  }, [fetchCustomers]);

  // Debounced customer search — fires on any input (no minimum chars)
  useEffect(() => {
    const trimmedInput = customerSearchInput.trim();

    // If cleared, restore full list
    if (trimmedInput.length === 0) {
      if (customerInitialLoadRef.current && customerSearchTermRef.current !== '') {
        customerSearchTermRef.current = '';
        setCustomerPage(1);
        setCustomerHasMore(true);
        fetchCustomers('', 1, false);
      }
      return;
    }

    if (customerSearchTermRef.current !== trimmedInput) {
      setCustomerPage(1);
      setCustomerHasMore(true);
      customerSearchTermRef.current = trimmedInput;
    }

    const timeoutId = setTimeout(() => {
      fetchCustomers(trimmedInput, 1, false);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [customerSearchInput, fetchCustomers]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleReset = () => {
    setFilters({
      selectedCustomer: '',
      fromDate: '',
      toDate: '',
      searchTerm: '',
    });
    setSelectedCustomer(null);
    setCustomerSearchInput('');
    setLedgerData([]);
    setPage(0);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Export functions
  const buildExportRows = () => {
    return filteredData.map((item, i) => ({
      'S.No': i + 1,
      'Date': item.date,
      'Particular': item.particular,
      'Debit (Rs.)': item.debit > 0 ? item.debit : 0,
      'Credit (Rs.)': item.credit > 0 ? item.credit : 0,
      'Balance (Rs.)': item.balance
    }));
  };

  const handleOpenExportDialog = (type) => {
    if (!selectedCustomer) {
      showSnackbar({ type: 'warning', message: 'Please select a customer first' });
      return;
    }
    setExportType(type);
    setExportDialogOpen(true);
    setExportFromDate(filters.fromDate || '');
    setExportToDate(filters.toDate || '');
    setExportDateMode('current');
  };

  const handleCloseExportDialog = () => {
    setExportDialogOpen(false);
    setExportType('');
    setExportLoading(false);
  };

  const handleConfirmExport = async () => {
    if (!selectedCustomer || !selectedCustomer.id) {
      showSnackbar({ type: 'warning', message: 'Please select a customer first.' });
      return;
    }
    setExportLoading(true);
    try {
      const params = {
        customerId: selectedCustomer.id,
        export: 'true',
      };

      // Apply date filters only if NOT in "All Dates (Full History)" mode
      if (exportDateMode !== 'all') {
        if (exportDateMode === 'current') {
          if (filters.fromDate) params.fromDate = filters.fromDate;
          if (filters.toDate) params.toDate = filters.toDate;
        } else if (exportDateMode === 'custom') {
          if (exportFromDate) params.fromDate = exportFromDate;
          if (exportToDate) params.toDate = exportToDate;
        } else if (exportDateMode === 'single') {
          if (exportFromDate) {
            params.fromDate = exportFromDate;
            params.toDate = exportFromDate;
          }
        }
      }

      const response = await getCustomerTransactionLedger(params);
      if (response.success && Array.isArray(response.data)) {
        // Map API response to ledger format
        const mappedData = response.data.map((item, index) => {
          return {
            sno: index + 1,
            date: item.date ? new Date(item.date).toISOString().split('T')[0] : '',
            particular: item.receivedType || 'Transaction',
            debit: item.transactionType === 'add' ? item.amount : 0,
            credit: item.transactionType !== 'add' ? item.amount : 0,
            customerId: item.customerId,
          };
        });

        // Sort by date ascending (oldest first) for correct balance calculation
        mappedData.sort((a, b) => new Date(a.date) - new Date(b.date));
        
        // Calculate running balance
        let runningBalance = 0;
        mappedData.forEach((item) => {
          runningBalance = runningBalance + item.debit - item.credit;
          item.balance = runningBalance;
        });

        // Sort by date descending (newest first) for display
        mappedData.sort((a, b) => new Date(b.date) - new Date(a.date));

        if (mappedData.length === 0) {
          setAlertModal({ open: true, title: 'Warning', message: 'No data found for the selected filter/date range.' });
          setExportLoading(false);
          return;
        }
        
        const rows = mappedData.map((item, i) => ({
          'S.No': i + 1,
          'Date': item.date,
          'Particular': item.particular,
          'Debit (Rs.)': item.debit > 0 ? item.debit : 0,
          'Credit (Rs.)': item.credit > 0 ? item.credit : 0,
          'Balance (Rs.)': item.balance
        }));

        const fileName = `Customer_Ledger_${selectedCustomer.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}`;

        if (exportType === 'csv') {
          exportAsCSV({ rows, fileNameBase: fileName });
        } else if (exportType === 'pdf') {
          const formattedRows = rows.map(r => ({
            ...r,
            'Debit (Rs.)': r['Debit (Rs.)'] > 0 ? `Rs. ${r['Debit (Rs.)'].toLocaleString('en-IN')}` : '-',
            'Credit (Rs.)': r['Credit (Rs.)'] > 0 ? `Rs. ${r['Credit (Rs.)'].toLocaleString('en-IN')}` : '-',
            'Balance (Rs.)': `Rs. ${r['Balance (Rs.)'].toLocaleString('en-IN')}`
          }));

          exportAsPDF({
            rows: formattedRows,
            title: 'Customer Ledger Report',
            fileNameBase: fileName,
          });
        } else if (exportType === 'excel') {
          exportAsExcel({
            rows,
            sheetName: 'Customer Ledger',
            fileNameBase: fileName
          });
        }

        handleCloseExportDialog();
      } else {
        setAlertModal({ open: true, title: 'Warning', message: 'No data found for the selected filter/date range.' });
      }
    } catch (error) {
      console.error('Export failed:', error);
      showSnackbar({ type: 'error', message: 'Export failed. Please try again.' });
    } finally {
      setExportLoading(false);
    }
  };

  // Filter ledger data based on search term
  const filteredData = ledgerData.filter(item => {
    if (!filters.searchTerm) return true;
    const search = filters.searchTerm.toLowerCase();
    return (
      item.particular.toLowerCase().includes(search) ||
      item.date.includes(search)
    );
  });

  const totalDebit = filteredData.reduce((sum, item) => sum + item.debit, 0);
  const totalCredit = filteredData.reduce((sum, item) => sum + item.credit, 0);
  const currentBalance = filteredData.length > 0 ? filteredData[filteredData.length - 1]?.balance : 0;

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
              Customer Ledger
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Track customer account transactions and balances
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'center' }}>
            <ExportButton
              onExportPdf={() => handleOpenExportDialog('pdf')}
              onExportCsv={() => handleOpenExportDialog('csv')}
              onExportExcel={() => handleOpenExportDialog('excel')}
            />
            <RefreshButton
              onClick={() => selectedCustomer && fetchCustomerLedger()}
              loading={loadingLedger}
              disabled={!selectedCustomer}
              sx={{
                fontWeight: 600,
                px: 2,
                fontSize: '15px',
                borderRadius: 2,
                height: { xs: 'auto', sm: 40 }
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Customer Selection & Filters */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Customer Selection & Filters
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Autocomplete
                options={customerOptions}
                getOptionLabel={(option) => option.name ? `${option.name} (${option.number})` : ''}
                value={selectedCustomer}
                onChange={(event, newValue) => {
                  setSelectedCustomer(newValue);
                  setPage(0);
                  // Clear ledger data when customer changes
                  if (!newValue) {
                    setLedgerData([]);
                  }
                }}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === 'input' || reason === 'clear') {
                    setCustomerSearchInput(newInputValue);
                  }
                }}
                onOpen={() => {
                  // List is pre-loaded on mount; only re-fetch if empty (e.g. after an error)
                  if (customerOptions.length === 0 && !customerFetchingRef.current) {
                    fetchCustomers('', 1, false);
                  }
                }}
                loading={loadingCustomers}
                ListboxProps={{
                  onScroll: (event) => {
                    const listboxNode = event.currentTarget;
                    const scrollTop = listboxNode.scrollTop;
                    const scrollHeight = listboxNode.scrollHeight;
                    const clientHeight = listboxNode.clientHeight;
                    const scrollBottom = scrollTop + clientHeight;
                    
                    if (scrollHeight > clientHeight && scrollBottom >= scrollHeight - 50) {
                      if (customerHasMore && !loadingCustomers && !customerFetchingRef.current) {
                        const nextPage = customerPage + 1;
                        setCustomerPage(nextPage);
                        fetchCustomers(customerSearchInput || '', nextPage, true);
                      }
                    }
                  },
                }}
                filterOptions={(options, { inputValue }) => {
                  const searchTerm = inputValue.toLowerCase();
                  return options.filter(option =>
                    (option.name || '').toLowerCase().includes(searchTerm) ||
                    String(option.number || '').toLowerCase().includes(searchTerm) ||
                    String(option.mobile || '').includes(searchTerm)
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Customer"
                    size="small"
                    placeholder="Type name, number, or mobile..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <React.Fragment>
                          {loadingCustomers ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {option.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        {option.number} • {option.mobile} {option.email ? `• ${option.email}` : ''}
                      </Typography>
                    </Box>
                  </Box>
                )}
                noOptionsText="No customers found"
                clearOnEscape
                selectOnFocus
                handleHomeEndKeys
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField 
                fullWidth 
                size="small" 
                type="date" 
                label="From Date" 
                InputLabelProps={{ shrink: true }} 
                value={filters.fromDate}
                onChange={(e) => setFilters({ ...filters, fromDate: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField 
                fullWidth 
                size="small" 
                type="date" 
                label="To Date" 
                InputLabelProps={{ shrink: true }} 
                value={filters.toDate}
                onChange={(e) => setFilters({ ...filters, toDate: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleReset}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  height: '40px',
                  borderRadius: 1.5
                }}
              >
                Reset
              </Button>
            </Grid>
          </Grid>
          
          {ledgerError && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#ffebee', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ color: '#d32f2f', fontWeight: 600 }}>
                {ledgerError}
              </Typography>
            </Box>
          )}
          
          {/* Search Results Summary */}
          {selectedCustomer && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#666' }}>
                Selected Customer: <strong>{selectedCustomer.name}</strong> ({selectedCustomer.number}) • 
                Mobile: {selectedCustomer.mobile} {selectedCustomer.email ? `• Email: ${selectedCustomer.email}` : ''}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {/* Table */}
      <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2, mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Ledger Details
          </Typography>
          <TableContainer>
            {loadingLedger ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#536DFE' }}>
                    <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>S.No</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Date</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Particular</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', textAlign: 'right' }}>Debit (Rs.)</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', textAlign: 'right' }}>Credit (Rs.)</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', textAlign: 'right' }}>Balance (Rs.)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body1" color="text.secondary">
                          {selectedCustomer ? 'No transactions found for this customer.' : 'Please select a customer to view ledger details'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => (
                  <TableRow 
                    key={row.sno} 
                    hover
                    sx={{
                      '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' },
                      '&:hover': { backgroundColor: '#f0f7ff !important' }
                    }}
                  >
                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{row.date}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{row.particular}</TableCell>
                        <TableCell sx={{ 
                          fontWeight: 600, 
                          color: row.debit > 0 ? '#d32f2f' : '#999',
                          textAlign: 'right',
                          fontFamily: 'monospace'
                        }}>
                          {row.debit > 0 ? `Rs. ${row.debit.toLocaleString('en-IN')}` : '-'}
                        </TableCell>
                        <TableCell sx={{ 
                          fontWeight: 600, 
                          color: row.credit > 0 ? '#4caf50' : '#999',
                          textAlign: 'right',
                          fontFamily: 'monospace'
                        }}>
                          {row.credit > 0 ? `Rs. ${row.credit.toLocaleString('en-IN')}` : '-'}
                        </TableCell>
                        <TableCell sx={{ 
                          fontWeight: 600, 
                          color: row.balance > 0 ? '#d32f2f' : '#4caf50',
                          textAlign: 'right',
                          fontFamily: 'monospace'
                        }}>
                          Rs. {row.balance.toLocaleString('en-IN')}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </TableContainer>
          
          {/* Pagination */}
          {filteredData.length > 0 && (
            <TablePagination
              
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </CardContent>
      </Card>

      {/* Export Date Range Dialog */}
      <Dialog
        open={exportDialogOpen}
        onClose={handleCloseExportDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 16px 48px rgba(83, 109, 254, 0.15), 0 2px 12px rgba(0,0,0,0.06)',
          }
        }}
      >
        {/* Gradient Header */}
        <Box sx={{
          background: 'linear-gradient(135deg, #536DFE 0%, #3d5afe 50%, #304ffe 100%)',
          px: 2.5, py: 1.8,
          position: 'relative', overflow: 'hidden',
        }}>
          <Box sx={{ position: 'absolute', top: -15, right: -15, width: 55, height: 55, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.08)' }} />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
              <Box sx={{
                width: 34, height: 34, borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.18)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <FileDownload sx={{ color: '#fff', fontSize: 18 }} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, color: '#fff', fontSize: '0.95rem', lineHeight: 1.2 }}>
                  Export Customer Ledger
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.68rem', mt: 0.2 }}>
                  Download your report data
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={handleCloseExportDialog} size="small" sx={{ color: 'rgba(255,255,255,0.8)', p: 0.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>

        <DialogContent sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
          {/* Format Badge */}
          <FormatBadge type={exportType} />

          {/* Section label */}
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#555', mb: 1, textTransform: 'uppercase', letterSpacing: 0.6 }}>
            Select Date Range
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
            {/* Option 1: Current Filters */}
            <Box
              onClick={() => setExportDateMode('current')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.2, borderRadius: 2, cursor: 'pointer',
                border: exportDateMode === 'current' ? '1.5px solid #536DFE' : '1.5px solid #e8eaf6',
                bgcolor: exportDateMode === 'current' ? 'rgba(83, 109, 254, 0.04)' : '#fff',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83, 109, 254, 0.02)' },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: exportDateMode === 'current' ? '#536DFE' : '#e8eaf6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease', flexShrink: 0,
              }}>
                <FilterAlt sx={{ fontSize: 16, color: exportDateMode === 'current' ? '#fff' : '#536DFE' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1a1a2e' }}>
                  Current Filters
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#999', mt: 0.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {filters.fromDate && filters.toDate
                    ? `${filters.fromDate} → ${filters.toDate}`
                    : filters.fromDate ? `From ${filters.fromDate}` : filters.toDate ? `Till ${filters.toDate}` : 'All dates'}
                </Typography>
              </Box>
              <Radio checked={exportDateMode === 'current'} size="small" sx={{ p: 0.3, color: '#c5cae9', '&.Mui-checked': { color: '#536DFE' } }} />
            </Box>

            {/* Option 2: Custom Date Range */}
            <Box
              onClick={() => setExportDateMode('custom')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.2, borderRadius: 2, cursor: 'pointer',
                border: exportDateMode === 'custom' ? '1.5px solid #536DFE' : '1.5px solid #e8eaf6',
                bgcolor: exportDateMode === 'custom' ? 'rgba(83, 109, 254, 0.04)' : '#fff',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83, 109, 254, 0.02)' },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: exportDateMode === 'custom' ? '#536DFE' : '#e8eaf6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease', flexShrink: 0,
              }}>
                <DateRange sx={{ fontSize: 16, color: exportDateMode === 'custom' ? '#fff' : '#536DFE' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1a1a2e' }}>
                  Custom Date Range
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#999', mt: 0.1 }}>
                  Choose from & to dates
                </Typography>
              </Box>
              <Radio checked={exportDateMode === 'custom'} size="small" sx={{ p: 0.3, color: '#c5cae9', '&.Mui-checked': { color: '#536DFE' } }} />
            </Box>

            {/* Option 3: Single Day */}
            <Box
              onClick={() => setExportDateMode('single')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.2, borderRadius: 2, cursor: 'pointer',
                border: exportDateMode === 'single' ? '1.5px solid #536DFE' : '1.5px solid #e8eaf6',
                bgcolor: exportDateMode === 'single' ? 'rgba(83, 109, 254, 0.04)' : '#fff',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83, 109, 254, 0.02)' },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: exportDateMode === 'single' ? '#536DFE' : '#e8eaf6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease', flexShrink: 0,
              }}>
                <Today sx={{ fontSize: 16, color: exportDateMode === 'single' ? '#fff' : '#536DFE' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1a1a2e' }}>
                  Single Day
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#999', mt: 0.1 }}>
                  Export for one specific date
                </Typography>
              </Box>
              <Radio checked={exportDateMode === 'single'} size="small" sx={{ p: 0.3, color: '#c5cae9', '&.Mui-checked': { color: '#536DFE' } }} />
            </Box>

            {/* Option 4: All Dates (Full History) */}
            <Box
              onClick={() => setExportDateMode('all')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.2, borderRadius: 2, cursor: 'pointer',
                border: exportDateMode === 'all' ? '1.5px solid #536DFE' : '1.5px solid #e8eaf6',
                bgcolor: exportDateMode === 'all' ? 'rgba(83, 109, 254, 0.04)' : '#fff',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83, 109, 254, 0.02)' },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: exportDateMode === 'all' ? '#536DFE' : '#e8eaf6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease', flexShrink: 0,
              }}>
                <CalendarMonth sx={{ fontSize: 16, color: exportDateMode === 'all' ? '#fff' : '#536DFE' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1a1a2e' }}>
                  All Data (Full History)
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#999', mt: 0.1 }}>
                  Export full database history without any filters
                </Typography>
              </Box>
              <Radio checked={exportDateMode === 'all'} size="small" sx={{ p: 0.3, color: '#c5cae9', '&.Mui-checked': { color: '#536DFE' } }} />
            </Box>
          </Box>

          {/* Date inputs — compact animated */}
          {exportDateMode === 'custom' && (
            <Box sx={{
              display: 'flex', gap: 1.5, mt: 1.5,
              p: 1.5, borderRadius: 2,
              bgcolor: '#fafbff', border: '1px solid #e8eaf6',
              animation: 'fadeIn 0.2s ease',
              '@keyframes fadeIn': { '0%': { opacity: 0, transform: 'translateY(-4px)' }, '100%': { opacity: 1, transform: 'translateY(0)' } },
            }}>
              <TextField
                label="From Date" type="date"
                value={exportFromDate}
                onChange={(e) => setExportFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: '#fff', fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontSize: '0.78rem' } }}
              />
              <TextField
                label="To Date" type="date"
                value={exportToDate}
                onChange={(e) => setExportToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: '#fff', fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontSize: '0.78rem' } }}
              />
            </Box>
          )}

          {exportDateMode === 'single' && (
            <Box sx={{
              mt: 1.5, p: 1.5, borderRadius: 2,
              bgcolor: '#fafbff', border: '1px solid #e8eaf6',
              animation: 'fadeIn 0.2s ease',
              '@keyframes fadeIn': { '0%': { opacity: 0, transform: 'translateY(-4px)' }, '100%': { opacity: 1, transform: 'translateY(0)' } },
            }}>
              <TextField
                label="Select Date" type="date"
                value={exportFromDate}
                onChange={(e) => setExportFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: '#fff', fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontSize: '0.78rem' } }}
              />
            </Box>
          )}
        </DialogContent>

        {/* Actions */}
        <Box sx={{ px: 2.5, py: 1.5, bgcolor: '#fafbff', borderTop: '1px solid #eef2f6', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button
            onClick={handleCloseExportDialog}
            variant="outlined" size="small"
            sx={{
              borderRadius: 2, textTransform: 'none', fontWeight: 600,
              px: 2, fontSize: '0.8rem', borderColor: '#c5cae9', color: '#536DFE',
              '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83,109,254,0.04)' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmExport}
            variant="contained" size="small"
            disabled={exportLoading || (exportDateMode === 'custom' && (!exportFromDate || !exportToDate)) || (exportDateMode === 'single' && !exportFromDate)}
            startIcon={exportLoading ? <CircularProgress size={14} color="inherit" /> : <FileDownload sx={{ fontSize: 16 }} />}
            sx={{
              borderRadius: 2, textTransform: 'none', fontWeight: 700,
              px: 2.5, fontSize: '0.8rem',
              background: 'linear-gradient(135deg, #536DFE 0%, #3d5afe 100%)',
              boxShadow: '0 3px 10px rgba(83, 109, 254, 0.3)',
              '&:hover': { background: 'linear-gradient(135deg, #3d5afe 0%, #304ffe 100%)', boxShadow: '0 4px 14px rgba(83, 109, 254, 0.4)' },
              '&.Mui-disabled': { background: '#e0e0e0', boxShadow: 'none' },
            }}
          >
            {exportLoading ? 'Exporting...' : 'Export Now'}
          </Button>
        </Box>
      </Dialog>
      <AlertModal open={alertModal.open} title={alertModal.title} message={alertModal.message} onClose={closeAlert} />
    </Box>
  );
};

export default CustomerLedger;

